import type {
  SystemSurfaceContext,
  SystemSurfaceInstance,
  SystemSurfaceManifest,
} from "../contracts/system-surface";

export interface SystemSurfaceHostHooks {
  onRunning?(surfaceId: SystemSurfaceManifest["id"]): void;
  onRequestHome?(surfaceId: SystemSurfaceManifest["id"]): void;
  onError?(surfaceId: SystemSurfaceManifest["id"], error: Error): void;
}

interface ActiveSystemSurface {
  generation: number;
  surfaceId: SystemSurfaceManifest["id"];
  host: HTMLElement;
  controller: AbortController;
  instance: SystemSurfaceInstance | null;
  cleaned: boolean;
}

function asError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}

export class SystemSurfaceHost {
  #generation = 0;
  #active: ActiveSystemSurface | null = null;

  constructor(private readonly hooks: SystemSurfaceHostHooks = {}) {}

  async open(manifest: SystemSurfaceManifest, host: HTMLElement): Promise<boolean> {
    if (this.#active) await this.close();

    const generation = ++this.#generation;
    const controller = new AbortController();
    const isCurrent = (): boolean => this.#active?.generation === generation;
    const context: SystemSurfaceContext = {
      signal: controller.signal,
      requestHome: () => {
        if (isCurrent()) this.hooks.onRequestHome?.(manifest.id);
      },
    };
    const active: ActiveSystemSurface = {
      generation,
      surfaceId: manifest.id,
      host,
      controller,
      instance: null,
      cleaned: false,
    };
    this.#active = active;

    try {
      const module = await manifest.load();
      if (!isCurrent()) return false;

      const instance = module.create(context);
      active.instance = instance;
      await instance.mount(host);
      if (!isCurrent()) return false;

      this.hooks.onRunning?.(manifest.id);
      return true;
    } catch (value) {
      if (!isCurrent()) return false;
      const error = asError(value);
      this.#active = null;
      await this.#cleanup(active);
      this.hooks.onError?.(manifest.id, error);
      return false;
    }
  }

  async close(): Promise<void> {
    ++this.#generation;
    const active = this.#active;
    this.#active = null;
    if (active) await this.#cleanup(active);
  }

  async #cleanup(active: ActiveSystemSurface): Promise<void> {
    if (active.cleaned) return;
    active.cleaned = true;
    active.controller.abort();
    try {
      await active.instance?.destroy();
    } catch (value) {
      this.hooks.onError?.(active.surfaceId, asError(value));
    }
    active.host.replaceChildren();
  }
}
